import { useState } from 'react';

import {
  Box,
  Card,
  Paper,
  Stack,
  styled,
  InputBase,
  IconButton,
  CardHeader,
  CardActionArea,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { mapirApiToken } from 'config';
import { useIntl } from 'react-intl';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export default function SearchAddress({ changeCenter }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const searchBackgroundColor = alpha(theme.palette.background.paper, isDarkMode ? 0.96 : 0.75);
  const searchTextColor = isDarkMode ? theme.palette.common.white : theme.palette.text.secondary;
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [data, setData] = useState([]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const searchFunction = (query) => {
    if (!query.trim()) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = 'https://map.ir/search/autocomplete';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': mapirApiToken,
        'Mapir-SDK': 'reactjs',
      },
      body: JSON.stringify({
        text: `${query.trim()} اصفهان`,
        returnid: true,
        location: {
          type: 'Point',
          coordinates: [51.666, 32.6539],
        },
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Map.ir search failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((result) => {
        setData(result?.value || []);
        setExpanded(true);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setAddress(value);
    searchFunction(value);
  };

  const handleSelect = (item) => {
    const coordinates = item?.geom?.coordinates || [item?.Coordinate?.lon, item?.Coordinate?.lat];
    changeCenter(coordinates);
    setExpanded(false);
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          color: searchTextColor,
          backgroundColor: searchBackgroundColor,
        }}
      >
        <IconButton sx={{ p: '10px', color: searchTextColor }} aria-label="menu">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{
            ml: 1,
            flex: 1,
            color: searchTextColor,
            '& input::placeholder': {
              color: searchTextColor,
              opacity: isDarkMode ? 0.8 : 0.7,
            },
          }}
          placeholder={formatMessage({ id: 'search_location' })}
          inputProps={{ 'aria-label': formatMessage({ id: 'search_location' }) }}
          value={address}
          onChange={handleChange}
        />
        {loading ? (
          <IconButton disabled color="primary" sx={{ p: '10px' }} aria-label="directions">
            <CircularProgress size={20} />
          </IconButton>
        ) : (
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
            sx={{ color: searchTextColor }}
          >
            <ExpandMoreIcon />
          </ExpandMore>
        )}
      </Paper>
      <Box sx={{ width: '100%', position: 'relative' }}>
        {expanded && (
          <Stack
            direction="column"
            rowGap={0.5}
            columnGap={1}
            sx={{
              position: 'absolute',
              top: 0,
              zIndex: 100,
              width: '100%',
              pt: 1,
            }}
          >
            {data.slice(0, 5).map((item, i) => (
              <Card
                elevation={2}
                key={i}
                sx={{
                  color: isDarkMode ? theme.palette.common.white : theme.palette.text.primary,
                  bgcolor: isDarkMode ? searchBackgroundColor : '#fffffff9',
                }}
              >
                <CardActionArea onClick={() => handleSelect(item)} sx={{ color: 'inherit' }}>
                  <CardHeader
                    title={
                      <Typography variant="subtitle2" color="inherit">
                        {item.title || item.Title}
                      </Typography>
                    }
                    subheader={item.address || item.Address}
                    subheaderTypographyProps={{
                      sx: {
                        color: isDarkMode ? theme.palette.common.white : theme.palette.text.secondary,
                        opacity: isDarkMode ? 0.8 : 1,
                      },
                    }}
                    action={
                      <Typography variant="caption" color="inherit">
                        {[item.county || item.City, item.region || item.Province].filter(Boolean).join(' - ')}
                      </Typography>
                    }
                    sx={{ py: 1 }}
                  />
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
